import { useState, useMemo } from 'react';
import { Reservation, DeletedReservation, ServiceBooking, Person } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatDayShort, formatPrice } from '@/lib/format-utils';
import {
    IconChevronDown,
    IconChevronUp,
    IconUser,
    IconCalendar,
    IconBuildingStore,
    IconTrash,
} from '@tabler/icons-react';
import { ServiceTitleStatic } from './services/service-title';
import { BookingGroupSize } from './snippets';
import SearchInput from './search-input';
import { filterByMultiWordSearch } from '@/utils/search-utils';
import SelectTimePopover from './select-time-popover';
import ServiceDatePopover from './service-date-popover';
import { ReservationPaymentStatus } from './reservation-ui';
import { TagIcon } from '@/lib/tag-utils';
import ShowSelfAttrs from './show-self-attrs';

export interface SimpleReservationsListProps {
    reservations?: Reservation[];
    deletedReservations?: DeletedReservation[];
    emptyMessage?: string;
}

export interface RowItem {
    id: number;
    reservation: Reservation;
    deletedBy?: string;
    deletedAt?: string;
}

export function SimpleReservationRow({ item }: { item: RowItem }): JSX.Element {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const { reservation, deletedBy, deletedAt } = item;

    const paxNames = useMemo(() => {
        const groupNames = (reservation.pax_groups || []).flatMap((g) => [
            g.name,
            ...(g.persons || []).map((p) => p.name || '')
        ]).filter(Boolean);
        const allNames = Array.from(new Set(groupNames));
        if (allNames.length === 0) return '';
        return allNames.join(', ');
    }, [reservation.pax_groups]);

    const formattedPrice = useMemo(() => {
        const totals = reservation.price_per_currency || {};
        const currencies = Object.keys(totals);
        if (currencies.length === 0) return null;
        return currencies
            .map((curr) => `$${formatPrice(totals[curr])} ${curr}`)
            .join(' / ');
    }, [reservation.price_per_currency]);

    const sortedBookings = useMemo(() => {
        const bookingsList = [...(reservation.service_bookings || [])];
        return bookingsList.sort((a, b) => {
            const aHasDate = Boolean(a.start_day || a.end_day);
            const bHasDate = Boolean(b.start_day || b.end_day);

            if (aHasDate && !bHasDate) return -1;
            if (!aHasDate && bHasDate) return 1;

            if (aHasDate && bHasDate) {
                const startDayDiff = (a.start_day || '').localeCompare(b.start_day || '');
                if (startDayDiff !== 0) return startDayDiff;
                return (a.start_hour || '').localeCompare(b.start_hour || '');
            }

            return 0;
        });
    }, [reservation.service_bookings]);

    return (
        <div className="border border-slate-200 rounded-xl bg-white overflow-hidden shadow-xs transition-shadow hover:shadow-md">
            {/* Top Succinct Summary Row */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                className={`
                    p-3 sm:p-4 flex flex-col md:flex-row md:items-start justify-between gap-3 cursor-pointer select-none
                    ${isExpanded ? 'bg-slate-50' : 'bg-white'}
                `}
            >
                <div className="flex flex-wrap items-start gap-3 min-w-0 flex-1">
                    {/* ID Badge Link */}
                    <Link
                        to={`/reservations/${reservation.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-md font-bold"
                    >
                        #{reservation.id}
                    </Link>

                    {/* Dates */}
                    <div className="flex items-start gap-1 text-xs text-slate-600 bg-slate-100/80 px-2.5 py-1 rounded-md">
                        <IconCalendar size={14} className="text-slate-400 shrink-0" />
                        <span>
                            {formatDayShort(reservation.start_day)} – {formatDayShort(reservation.end_day)}
                        </span>
                    </div>

                    <div className="flex items-start gap-1 text-xs text-slate-700 font-normal solid-box-border-blue">
                        <IconBuildingStore size={13} className="text-slate-400 shrink-0" />
                        <span className="truncate max-w-[140px] sm:max-w-[200px]">
                            {reservation.client_business?.name || t('no_client_assigned')}
                        </span>
                    </div>

                    {/* Prominent Pax Names */}
                    {paxNames && (
                        <div className="flex items-start gap-1.5 min-w-0">
                            <IconUser size={15} className="text-slate-600 shrink-0" />
                            <span className="font-bold text-slate-900 text-sm truncate max-w-[200px] sm:max-w-[320px]">
                                {paxNames}
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex items-start justify-between md:justify-end gap-6 shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-slate-100">
                    <div className='flex gap-1 items-start'>
                        <ReservationPaymentStatus reservation={reservation} />
                        {/* Price */}
                        {formattedPrice && (
                            <div className="text-sm font-bold text-slate-800 bg-emerald-50 text-emerald-800 border border-emerald-200/60 px-2.5 py-1 rounded-md shrink-0">
                                {formattedPrice}
                            </div>
                        )}
                    </div>

                    {/* Expand/Collapse Icon */}
                    <button
                        type="button"
                        className="p-1 rounded-md text-slate-400 transition-colors shrink-0"
                    >
                        {isExpanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
                    </button>
                </div>
            </div>

            {/* Expandable Section: Service Bookings */}
            {isExpanded && (
                <div className="border-t border-slate-100 bg-slate-50/50 p-3 sm:p-4 space-y-3">
                    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100 shadow-2xs">
                        {sortedBookings.map((booking: ServiceBooking) => (
                            <div
                                key={booking.id}
                                className="p-3 flex flex-col sm:flex-row sm:items-start justify-between gap-3"
                            >
                                <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                    <div className="p-1 bg-slate-100 text-slate-700 rounded-lg border border-slate-200 text-xxs">
                                        #{booking.id}
                                    </div>
                                    <div className="whitespace-nowrap flex items-start gap-4 justify-around text-slate-500 w-[180px]">
                                        <ServiceDatePopover
                                            icon={true}
                                            size='xs'
                                            serviceCategory={booking.service_data?.category || booking.category || ''}
                                            value={[booking.start_day, booking.end_day]}
                                            onChange={() => { }}
                                            disabled={true}
                                        />
                                        <SelectTimePopover
                                            icon={true}
                                            size='xs'
                                            value={booking.start_hour}
                                            onChange={() => { }}
                                            disabled={true}
                                        />
                                    </div>
                                    <div className='flex-1 flex items-start gap-4 text-xxs'>
                                        <TagIcon name={booking.service_data.category} hideText noBackground size='xs' />
                                        <div className='space-y-2'>
                                            <div className='text-xs font-medium'>
                                                {ServiceTitleStatic(booking.service_data)} <span className='text-xxs font-light text-slate-600 bg-gray-100 px-2 py-0.5 rounded-full'>#{booking.service_data.id}</span>
                                            </div>
                                            <div className='text-xs'>
                                                <ShowSelfAttrs
                                                    size='xxs'
                                                    className='flex gap-1 flex-wrap'
                                                    showkey={false}
                                                    attrs={booking.self_attrs || []}
                                                />
                                            </div>
                                        </div>
                                        {/* <ServiceTitle service={booking.service_data} links={false} /> */}
                                    </div>
                                </div>

                                <div className="bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 rounded text-[11px]">
                                    {booking?.selected_provider?.name || '--'}
                                </div>

                                <div className="text-xs space-y-2">
                                    <BookingGroupSize
                                        booking={booking}
                                    />

                                    <div className='flex items-end justify-end gap-2 w-full'>
                                        <div className="font-bold text-red-600">
                                            <span className='font-light text-gray-600'>{t('cost')}:</span>
                                            ${formatPrice(booking.money_out_amount)} <span className="text-[10px] text-red-400 uppercase">{booking.money_out_currency}</span>
                                        </div>
                                        <div className='text-gray-300'>|</div>
                                        <div className="font-bold text-slate-800">
                                            <span className='font-light text-gray-600'>{t('price')}:</span>
                                            ${formatPrice(booking.money_in_amount)} <span className="text-[10px] text-slate-500 uppercase">{booking.money_in_currency}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {reservation.observations && (
                        <div className='my-2 solid-box-border-negative text-xs'>
                            {reservation.observations}
                        </div>
                    )}

                    {/* Deleted Info at the Bottom in Gray */}
                    {(deletedBy || deletedAt) && (
                        <div className="text-xs text-slate-400 flex flex-wrap items-start gap-4">
                            {deletedBy && (
                                <span>{t('deleted_by')}: {deletedBy}</span>
                            )}
                            {deletedAt && (
                                <span>{t('deleted_at')}: {new Date(deletedAt).toLocaleString()}</span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function SimpleReservationsList({
    reservations,
    deletedReservations,
    emptyMessage,
}: SimpleReservationsListProps): JSX.Element {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');

    const items = useMemo<RowItem[]>(() => {
        if (deletedReservations && deletedReservations.length > 0) {
            return deletedReservations
                .filter((dr) => Boolean(dr.data))
                .map((dr) => ({
                    id: dr.id,
                    reservation: dr.data,
                    deletedBy: dr.deleted_by?.name || dr.deleted_by?.email,
                    deletedAt: dr.deleted_at,
                }));
        }
        if (reservations && reservations.length > 0) {
            return reservations.map((r) => ({
                id: r.id,
                reservation: r,
            }));
        }
        return [];
    }, [reservations, deletedReservations]);

    const filteredItems = useMemo(() => {
        if (!searchQuery.trim()) return items;
        return filterByMultiWordSearch(items, searchQuery, (item) => {
            const res = item.reservation;
            const resId = String(res.id || '');
            const clientName = res.client_business?.name || '';
            const paxNames = (res.pax_groups || []).flatMap((g) => [
                g.name || '',
                ...(g.persons || []).map((p) => p.name || '')
            ]).join(' ');
            const deletedBy = item.deletedBy || '';
            return `${resId} ${clientName} ${paxNames} ${deletedBy}`;
        });
    }, [items, searchQuery]);

    if (items.length === 0) {
        return (
            <div className="p-8 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                <IconTrash className="mx-auto h-8 w-8 text-gray-400 mb-2" stroke={1.5} />
                <p className="text-sm text-gray-500 font-medium">
                    {emptyMessage || t('no_deleted_reservations')}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <SearchInput
                onSearch={(value) => setSearchQuery(value)}
                placeholder={t('search')}
                size="xs"
                initialValue={searchQuery}
                className="w-full mb-2"
            />

            {filteredItems.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400 italic bg-slate-50 rounded-lg border border-dashed border-slate-200">
                    {t('no_results')}
                </div>
            ) : (
                filteredItems.map((item) => (
                    <SimpleReservationRow key={item.id} item={item} />
                ))
            )}
        </div>
    );
}
